import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  GuardsCheckStart,
  ResolveEnd,
  ResolveStart,
  Router,
} from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  take,
  tap,
} from 'rxjs/operators';
import { WfDialogService } from 'wflow-ui';
import { SetCurrentOrganization } from '../organizations/organizations.actions';
import {
  Navigated,
  Navigation,
  NavigationInit,
  NavigationStart,
} from './router.actions';
import { routeSerializer } from './utils/router-effect.utils';

@Injectable()
export class RouterEffects {
  readonly navigationInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NavigationInit),
      mergeMap(() => this._initRouterStore()),
    ),
  );

  readonly navigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Navigation),
      distinctUntilChanged(),
      map((payload: { snapshot: ActivatedRouteSnapshot }) =>
        routeSerializer(payload.snapshot),
      ),
      tap((route) => {
        return this.store.dispatch(
          SetCurrentOrganization({
            orgId: route.params.orgId?.toLowerCase() ?? null,
          }),
        );
      }),
      map((route) => Navigated({ state: route })),
    ),
  );

  readonly navigationStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NavigationStart),
      distinctUntilChanged(),
      tap(() => this.dialogs.closeAll()),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private dialogs: WfDialogService,
    private router: Router,
    private destroyRef: DestroyRef,
  ) {}

  /**
   * Init listen router events and record here to store.
   */
  private _initRouterStore() {
    const initial = this.router.events.pipe(
      filter((event) => event instanceof GuardsCheckStart),
      map((event: GuardsCheckStart) => event.state.root),
      take(1),
    );
    const fromEvent = this.router.events.pipe(
      filter((event) => event instanceof ResolveEnd),
      map((event: ResolveEnd) => event?.state?.root),
    );

    const navigation = merge(initial, fromEvent).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((snapshot: ActivatedRouteSnapshot) => Navigation({ snapshot })),
    );

    const navigationStart = this.router.events.pipe(
      filter((event) => event instanceof ResolveStart),
      distinctUntilChanged(),
      takeUntilDestroyed(),
      map(() => NavigationStart()),
    );

    return merge(navigation, navigationStart);
  }
}
